import { FC } from 'react';
import { css } from '@emotion/react';
import { colors, spacing } from '../../../styles';

interface ParsedToolOutput {
  type?: string;
  status?: string;
  message?: string;
  [key: string]: any;
}

interface ToolRendererProps {
  toolName: string;
  output: ParsedToolOutput;
  isLoading?: boolean;
}

// Helper function to parse technical insights
const parseTechnicalInsights = (insights: string) => {
  const sections: {
    movingAverages: string[];
    trend: string[];
    rsi: string[];
    bollingerBands: string[];
    volatility: string[];
  } = {
    movingAverages: [],
    trend: [],
    rsi: [],
    bollingerBands: [],
    volatility: [],
  };

  if (!insights) return sections;

  // Split by periods and filter out empty strings
  const sentences = insights.split('. ').filter((s) => s.trim().length > 0);

  sentences.forEach((sentence) => {
    const trimmed = sentence.trim();
    if (trimmed.includes('Moving Averages:') || trimmed.includes('SMA') || trimmed.includes('EMA')) {
      sections.movingAverages.push(trimmed);
    } else if (trimmed.includes('Trend:') || trimmed.includes('crossover')) {
      sections.trend.push(trimmed);
    } else if (trimmed.includes('RSI:')) {
      sections.rsi.push(trimmed);
    } else if (
      trimmed.includes('Bollinger Bands:') ||
      trimmed.includes('Upper:') ||
      trimmed.includes('Middle:') ||
      trimmed.includes('Lower:')
    ) {
      sections.bollingerBands.push(trimmed);
    } else if (trimmed.includes('Volatility:') || trimmed.includes('volatility')) {
      sections.volatility.push(trimmed);
    }
  });

  return sections;
};

export const TechnicalRenderer: FC<ToolRendererProps> = ({ output, isLoading }) => {
  if (isLoading) {
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.silver};
        `}
      >
        Analyzing technicals...
      </div>
    );
  }

  // Parse the output structure - check for new format first
  const symbol = typeof output === 'object' && output ? output.symbol || '' : '';
  const insights =
    typeof output === 'object' && output
      ? output.insights || output.content || ''
      : typeof output === 'string'
        ? output
        : '';

  if (!insights) {
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.silver};
        `}
      >
        Technical analysis completed
      </div>
    );
  }

  // Parse the insights into structured sections
  const sections = parseTechnicalInsights(insights);
  const hasStructuredData = Object.values(sections).some((section) => section.length > 0);

  if (!hasStructuredData) {
    // Fallback to simple display if parsing fails
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.liquorice};
          line-height: 1.4;

          html[data-theme='dark'] & {
            color: ${colors.sugar};
          }
        `}
      >
        <div
          css={css`
            margin-bottom: ${spacing.centi};
          `}
        >
          {symbol && `Technical Analysis - ${symbol}`}
        </div>
        <div
          css={css`
            font-size: 13px;
            color: ${colors.silver};
            white-space: pre-line;
          `}
        >
          {insights}
        </div>
      </div>
    );
  }

  return (
    <div
      css={css`
        color: ${colors.liquorice};
        font-size: 14px;
        line-height: 1.4;

        html[data-theme='dark'] & {
          color: ${colors.sugar};
        }
      `}
    >
      {/* Header */}
      {symbol && (
        <div
          css={css`
            font-weight: 600;
            margin-bottom: ${spacing.centi};
          `}
        >
          Technical Analysis - {symbol}
        </div>
      )}

      {/* Moving Averages Section */}
      {sections.movingAverages.length > 0 && (
        <div
          css={css`
            margin-bottom: ${spacing.deci};
          `}
        >
          <div
            css={css`
              font-weight: 500;
              font-size: 13px;
              color: ${colors.petrolBlue};
              margin-bottom: ${spacing.milli};

              html[data-theme='dark'] & {
                color: ${colors.skyBlue};
              }
            `}
          >
            Moving Averages
          </div>
          {sections.movingAverages.map((ma, idx) => (
            <div
              key={idx}
              css={css`
                font-size: 13px;
                color: ${colors.tungsten};
                margin-bottom: ${spacing.milli};
                padding-left: ${spacing.deci};

                html[data-theme='dark'] & {
                  color: ${colors.cloud};
                }
              `}
            >
              {ma.replace('Moving Averages:', '').trim()}
            </div>
          ))}
        </div>
      )}

      {/* Trend Section */}
      {sections.trend.length > 0 && (
        <div
          css={css`
            margin-bottom: ${spacing.deci};
          `}
        >
          <div
            css={css`
              font-weight: 500;
              font-size: 13px;
              color: ${colors.petrolBlue};
              margin-bottom: ${spacing.milli};

              html[data-theme='dark'] & {
                color: ${colors.skyBlue};
              }
            `}
          >
            Trend Analysis
          </div>
          {sections.trend.map((trend, idx) => (
            <div
              key={idx}
              css={css`
                font-size: 13px;
                color: ${colors.tungsten};
                margin-bottom: ${spacing.milli};
                padding-left: ${spacing.deci};

                html[data-theme='dark'] & {
                  color: ${colors.cloud};
                }
              `}
            >
              {trend.replace('Trend:', '').trim()}
            </div>
          ))}
        </div>
      )}

      {/* RSI Section */}
      {sections.rsi.length > 0 && (
        <div
          css={css`
            margin-bottom: ${spacing.deci};
          `}
        >
          <div
            css={css`
              font-weight: 500;
              font-size: 13px;
              color: ${colors.petrolBlue};
              margin-bottom: ${spacing.milli};

              html[data-theme='dark'] & {
                color: ${colors.skyBlue};
              }
            `}
          >
            RSI Indicator
          </div>
          {sections.rsi.map((rsi, idx) => (
            <div
              key={idx}
              css={css`
                font-size: 13px;
                color: ${colors.tungsten};
                margin-bottom: ${spacing.milli};
                padding-left: ${spacing.deci};

                html[data-theme='dark'] & {
                  color: ${colors.cloud};
                }
              `}
            >
              {rsi.replace('RSI:', '').trim()}
            </div>
          ))}
        </div>
      )}

      {/* Bollinger Bands Section */}
      {sections.bollingerBands.length > 0 && (
        <div
          css={css`
            margin-bottom: ${spacing.deci};
          `}
        >
          <div
            css={css`
              font-weight: 500;
              font-size: 13px;
              color: ${colors.petrolBlue};
              margin-bottom: ${spacing.milli};

              html[data-theme='dark'] & {
                color: ${colors.skyBlue};
              }
            `}
          >
            Bollinger Bands
          </div>
          {sections.bollingerBands.map((bb, idx) => (
            <div
              key={idx}
              css={css`
                font-size: 13px;
                color: ${colors.tungsten};
                margin-bottom: ${spacing.milli};
                padding-left: ${spacing.deci};

                html[data-theme='dark'] & {
                  color: ${colors.cloud};
                }
              `}
            >
              {bb.replace('Bollinger Bands:', '').trim()}
            </div>
          ))}
        </div>
      )}

      {/* Volatility Section */}
      {sections.volatility.length > 0 && (
        <div>
          <div
            css={css`
              font-weight: 500;
              font-size: 13px;
              color: ${colors.petrolBlue};
              margin-bottom: ${spacing.milli};

              html[data-theme='dark'] & {
                color: ${colors.skyBlue};
              }
            `}
          >
            Volatility
          </div>
          {sections.volatility.map((vol, idx) => (
            <div
              key={idx}
              css={css`
                font-size: 13px;
                color: ${colors.tungsten};
                margin-bottom: ${spacing.milli};
                padding-left: ${spacing.deci};

                html[data-theme='dark'] & {
                  color: ${colors.cloud};
                }
              `}
            >
              {vol.replace('Volatility:', '').trim()}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
